<template>
  <div class="flex h-screen overflow-hidden bg-surface-0 w-full relative">

    <!-- Mobile Sidebar -->
    <Sidebar v-model:visible="navVisible" :baseZIndex="1000" class="w-72 lg:hidden p-0">
      <template #header>
        <div class="flex items-center gap-2 min-w-0">
          <i class="pi pi-graduation-cap text-primary" style="font-size: 1.5rem;"></i>
          <span class="font-bold text-lg text-color truncate">{{ schoolName }}</span>
        </div>
      </template>

      <div class="px-4 py-3 border-b border-surface">
        <label class="text-xs font-semibold text-muted-color uppercase tracking-wider">Viewing</label>
        <GuardianChildSwitcher class="mt-1" />
      </div>

      <div class="px-2 py-3">
        <ul class="flex flex-col gap-1" @click="navVisible = false">
          <SidebarMenuItem
            v-for="item in menuItems"
            :key="item.routeName"
            :routeName="item.routeName"
            :tooltipText="item.tooltipText"
            :menuText="item.menuText"
            :menuIcon="item.menuIcon"
            :isCollapsed="false"
            :isActiveRoute="isActiveMenuPath(route.path, item.path)"
            :badgeCount="item.routeName === 'parent-messages' ? totalUnread : undefined"
          />
        </ul>
        <Button
          label="Log out"
          icon="pi pi-sign-out"
          text
          severity="danger"
          class="!w-full !justify-start mt-4"
          @click="showLogoutDialog = true"
        />
      </div>
    </Sidebar>

    <!-- Desktop Sidebar -->
    <aside
      :class="[
        'hidden lg:flex flex-col overflow-y-hidden duration-300 ease-linear border-r border-surface z-20 bg-surface-0',
        isCollapsed ? 'w-[80px] items-center' : 'w-56',
      ]"
      class="h-screen transition-all"
    >
      <div class="flex items-center justify-between gap-2 px-6 h-[80px] shrink-0">
        <div class="flex gap-2 items-center self-stretch my-auto overflow-hidden">
          <i class="pi pi-graduation-cap text-primary" :class="isCollapsed ? 'text-2xl' : 'text-3xl'"></i>
          <div v-if="!isCollapsed" class="my-auto font-bold text-lg truncate w-[180px]" :title="schoolName">
            {{ schoolName }}
          </div>
        </div>
      </div>

      <div v-if="!isCollapsed" class="px-4 pb-3 border-b border-surface w-full">
        <label class="text-xs font-semibold text-muted-color uppercase tracking-wider">Viewing</label>
        <GuardianChildSwitcher class="mt-1" size="small" />
      </div>

      <div class="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear flex-1 w-full">
        <div class="flex flex-col w-full px-2">
          <div v-if="!isCollapsed" class="px-4 py-4 text-xs font-semibold tracking-wider text-muted-color uppercase">
            Main menu
          </div>
          <ul class="flex flex-col justify-center gap-1 w-full pb-4">
            <SidebarMenuItem
              v-for="item in menuItems"
              :key="item.routeName"
              :routeName="item.routeName"
              :tooltipText="item.tooltipText"
              :menuText="item.menuText"
              :menuIcon="item.menuIcon"
              :isCollapsed="isCollapsed"
              :isActiveRoute="isActiveMenuPath(route.path, item.path)"
              :badgeCount="item.routeName === 'parent-messages' ? totalUnread : undefined"
            />
          </ul>
          <Button
            v-tooltip.right="isCollapsed ? 'Log out' : null"
            :label="isCollapsed ? undefined : 'Log out'"
            icon="pi pi-sign-out"
            text
            severity="danger"
            :class="isCollapsed ? '' : '!w-full !justify-start'"
            class="mb-10"
            aria-label="Log out"
            @click="showLogoutDialog = true"
          />
        </div>
      </div>
    </aside>

    <!-- Main Content -->
    <div class="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden h-full w-full min-w-0 bg-surface-50">

      <Toolbar class="bg-surface-0 shadow-sm !px-3 md:!px-4 !py-2 sticky top-0 z-30 !rounded-none !border-0 !border-b border-surface">
        <template #start>
          <div class="flex items-center gap-2 md:gap-3 min-w-0">
            <Button icon="pi pi-bars" rounded text class="lg:!hidden" aria-label="Open menu" @click="navVisible = true" />
            <Button icon="pi pi-bars" rounded text class="!hidden lg:!inline-flex" aria-label="Collapse menu" @click="isCollapsed = !isCollapsed" />
            <div class="flex flex-col min-w-0">
              <span class="font-bold text-color text-base md:text-lg truncate" data-testid="parent-page-title">{{ pageTitle }}</span>
              <span class="text-xs text-muted-color hidden lg:block">Parent Portal</span>
            </div>
          </div>
        </template>

        <template #end>
          <Button
            :label="userInitials"
            rounded
            class="!w-10 !h-10 !p-0 font-semibold"
            aria-label="Open account menu"
            data-testid="parent-account-button"
            @click="togglePanel($event)"
          />

          <OverlayPanel ref="panel" dismissable class="w-72 max-w-[calc(100vw-2rem)]">
            <div class="flex items-center gap-3 pb-3 border-b border-surface">
              <div class="w-10 h-10 rounded-full bg-primary text-primary-contrast flex items-center justify-center font-semibold text-sm shrink-0">
                {{ userInitials }}
              </div>
              <div class="overflow-hidden">
                <p class="text-sm font-semibold text-color truncate">{{ userName }}</p>
                <p class="text-xs text-muted-color truncate">{{ schoolName }}</p>
              </div>
            </div>

            <div class="mt-3 flex flex-col gap-1">
              <Button
                v-if="hasStaffDashboard"
                label="Staff dashboard"
                icon="pi pi-briefcase"
                text
                severity="secondary"
                class="!justify-start"
                data-testid="switch-to-staff"
                @click="goTo('admin-home')"
              />
              <Button
                v-if="hasMultipleSchools"
                label="Switch school"
                icon="pi pi-building"
                text
                severity="secondary"
                class="!justify-start"
                data-testid="open-school-switcher"
                @click="openSchoolSwitcher"
              />
              <Button label="My profile" icon="pi pi-user" text severity="secondary" class="!justify-start" @click="goTo('parent-profile')" />
              <Button label="Settings" icon="pi pi-cog" text severity="secondary" class="!justify-start" @click="goTo('parent-settings')" />
              <Button label="Log out" icon="pi pi-sign-out" text severity="danger" class="!justify-start" @click="showLogoutDialog = true" />
            </div>
          </OverlayPanel>
        </template>
      </Toolbar>

      <!-- Child context banner — shown on child-specific pages -->
      <div
        v-if="showChildBanner && selectedChild"
        class="bg-surface-0 border-b border-surface px-3 md:px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-2"
        data-testid="child-banner"
      >
        <div class="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs shrink-0">
          {{ childInitials(selectedChild.student) }}
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-color truncate">{{ childFullName(selectedChild.student) }}</p>
          <p class="text-xs text-muted-color truncate">{{ childPlacementLabel(selectedChild) }}</p>
        </div>
        <GuardianChildSwitcher v-if="children.length > 1" size="small" :fluid="false" class="w-full sm:w-56" />
      </div>

      <div class="flex-1 w-full min-w-0">
        <router-view />
      </div>
    </div>

    <Dialog v-model:visible="showLogoutDialog" modal header="Log out" :style="{ width: '90vw', maxWidth: '350px' }">
      <p class="mb-4">Are you sure you want to log out?</p>
      <template #footer>
        <Button label="Cancel" text severity="secondary" @click="showLogoutDialog = false" />
        <Button label="Log out" severity="danger" data-testid="confirm-logout" @click="confirmLogout" />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="schoolSwitcherOpen"
      modal
      header="Switch school"
      :style="{ width: '34rem', maxWidth: 'calc(100vw - 2rem)' }"
      :draggable="false"
    >
      <SchoolPicker v-if="schoolSwitcherOpen" returnTo="/parent/home" @cancel="schoolSwitcherOpen = false" />
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import OverlayPanel from 'primevue/overlaypanel';
import Sidebar from 'primevue/sidebar';
import Toolbar from 'primevue/toolbar';
import SidebarMenuItem from '@/components/common/SidebarMenuItem.vue';
import SchoolPicker from '@/components/common/SchoolPicker.vue';
import GuardianChildSwitcher from '@/components/guardianPortal/GuardianChildSwitcher.vue';
import { useUserStore } from '@/store/userStore';
import { can, AppPermissions } from '@/utils/permissions';
import { useChatNotifications } from '@/composables/useChatNotifications';
import { useGuardianChildSelection } from '@/composables/guardianPortal/useGuardianChildSelection';
import { provideGuardianPortalContext } from '@/composables/guardianPortal/useGuardianPortalContext';
import { useHasMultipleSchools } from '@/composables/queries/useHasMultipleSchools';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { childFullName, childInitials, childPlacementLabel } from '@/utils/guardianPortal';
import {
  guardianMenuItems,
  guardianPageTitle,
  isActiveMenuPath,
  isChildSpecificRoute,
  nameInitials,
} from '@/utils/guardianPortalNavigation';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();

const navVisible = ref(false);
const showLogoutDialog = ref(false);
const schoolSwitcherOpen = ref(false);
const isCollapsed = ref(false);

const {
  children,
  selectedChildId,
  selectedChild,
  loading: childrenLoading,
  error: childrenError,
  refetch,
} = useGuardianChildSelection();
// Unread polling only for accounts that can use the school chat at all.
const canChat = can(userStore.currentUser, AppPermissions.Chat.Actions.View, AppPermissions.Chat.Resource);
const totalUnread = canChat ? useChatNotifications().totalUnread : ref(0);

provideGuardianPortalContext({
  children,
  selectedChildId,
  childId: computed(() => (selectedChild.value ? String(selectedChild.value.student.id) : '')),
  selectedChild,
  childrenLoading,
  childrenError,
  refetchChildren: () => { void refetch(); },
});

// School colours and logo on the shared domain (school domains get them from
// ResolveSchool): getSchool is cache-first and applies the branding it returns.
useSchoolQuery();

const menuItems = computed(() => guardianMenuItems(userStore.isTertiary, canChat));
const pageTitle = computed(() => guardianPageTitle(route.name));
const showChildBanner = computed(() => isChildSpecificRoute(route.name));
const schoolName = computed(() => userStore.currentSchool?.name || 'School');

const userName = computed(() => {
  const user = userStore.currentUser as { __typename?: string; name?: string | null } | null;
  return user?.__typename === 'User' && user.name ? user.name : 'Parent / Guardian';
});
const userInitials = computed(() => nameInitials(userName.value === 'Parent / Guardian' ? '' : userName.value));

// Staff who are also guardians move between the two portals from here.
const hasStaffDashboard = computed(() => !!userStore.currentUser && !userStore.isGuardianOnly);
const hasMultipleSchools = useHasMultipleSchools(computed(() => userStore.userSchools.length));

const panel = ref<InstanceType<typeof OverlayPanel> | null>(null);
const togglePanel = (event: Event) => panel.value?.toggle(event);

function goTo(name: string) {
  panel.value?.hide();
  router.push({ name });
}

function openSchoolSwitcher() {
  panel.value?.hide();
  schoolSwitcherOpen.value = true;
}

const checkViewportSize = () => {
  isCollapsed.value = window.innerWidth < 1400;
};

onMounted(() => {
  checkViewportSize();
  window.addEventListener('resize', checkViewportSize);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkViewportSize);
});

async function confirmLogout() {
  showLogoutDialog.value = false;
  try {
    await userStore.logout();
  } catch (error) {
    console.error('Logout did not complete cleanly', error);
  }
  window.location.href = '/login';
}
</script>

<style>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
